// Destructible forest. Trees of one type all have an identical vertex count (the builders in
// Scene.ts randomise vertex POSITIONS, never topology), so we can pack every tree of a type into
// one big buffer as fixed-size "slots" and fell an individual tree with a swap-remove:
//
//   1. copy the LAST live slot's vertices over the felled slot   (one TypedArray.copyWithin)
//   2. shrink the draw range by one slot                          (no upload — just a draw param)
//   3. upload ONLY the one moved slot                             (addUpdateRange → tiny bufferSubData)
//
// Because each tree's world transform AND its random size/jitter are baked into its vertices,
// swapping two trees' slots in the buffer never moves them in the world or makes them look alike —
// so we keep the existing per-tree variety with zero visual regression, at ~2 draw calls per type
// instead of the old global material-merge. A separate stump buffer grows as trees fall.
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export type TreeType = 'conifer' | 'broadleaf' | 'autumn' | 'dead';

// The vertex attributes we carry. position/normal for everything; uv/color only on textured
// foliage canopies. Every slot in a bucket has the same set, so copies are uniform memmoves.
interface PackedAttr {
  name: 'position' | 'normal' | 'uv' | 'color';
  comp: number;          // components per vertex (3 for pos/normal/color, 2 for uv)
  arr: Float32Array;     // full backing array (capacity * slotVerts * comp)
  attr: THREE.BufferAttribute;
}

interface Bucket {
  slotVerts: number;     // vertices one tree contributes to THIS material
  attrs: PackedAttr[];
  geo: THREE.BufferGeometry;
}

interface Handle { x: number; z: number; gy: number; slot: number; }

interface TypePack {
  buckets: Bucket[];
  live: number;          // number of slots currently drawn
  slotToHandle: Handle[]; // index = slot, length tracks `live`
}

/** One tree, baked into per-material geometries in a deterministic order. */
function bakeTree(obj: THREE.Object3D): { mat: THREE.Material; geo: THREE.BufferGeometry }[] {
  obj.updateMatrixWorld(true);
  const byMat = new Map<THREE.Material, THREE.BufferGeometry[]>();
  const order: THREE.Material[] = [];
  obj.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!(m as unknown as { isMesh?: boolean }).isMesh) return;
    if (Array.isArray(m.material)) return;
    let geo = m.geometry.clone();
    if (geo.index) geo = geo.toNonIndexed();
    if (!geo.getAttribute('normal')) geo.computeVertexNormals();
    const textured = !!(m.material as THREE.MeshStandardMaterial).map;
    const hasColor = !!geo.getAttribute('color');
    for (const name of Object.keys(geo.attributes)) {
      if (name === 'position' || name === 'normal') continue;
      if (name === 'uv' && textured) continue;
      if (name === 'color' && hasColor) continue;
      geo.deleteAttribute(name);
    }
    geo.applyMatrix4(m.matrixWorld);
    if (!byMat.has(m.material)) { byMat.set(m.material, []); order.push(m.material); }
    byMat.get(m.material)!.push(geo);
  });
  // Merge the meshes that share a material (e.g. a dead tree's trunk + 4 branches) within the tree.
  return order.map((mat) => ({ mat, geo: mergeGeometries(byMat.get(mat)!, false)! }));
}

const STUMP_MAT = new THREE.MeshStandardMaterial({ color: 0x3a281a, roughness: 1, flatShading: true });
function stumpTemplate(): THREE.BufferGeometry {
  // A short jagged stub; base sits at y=0 so a per-stump (x, gy, z) offset seats it on the terrain.
  let g: THREE.BufferGeometry = new THREE.CylinderGeometry(2.0, 3.2, 5.5, 6, 1);
  g.translate(0, 2.75, 0);
  if (g.index) g = g.toNonIndexed();
  if (!g.getAttribute('normal')) g.computeVertexNormals();
  return g;
}

export class Forest {
  readonly group = new THREE.Group();
  private pending = new Map<TreeType, { obj: THREE.Object3D; x: number; z: number; gy: number }[]>();
  private packs = new Map<TreeType, TypePack>();

  // Stump buffer: grows from 0 up to the total tree count as trees are felled.
  private stumpPos!: THREE.BufferAttribute;
  private stumpNorm!: THREE.BufferAttribute;
  private stumpGeo!: THREE.BufferGeometry;
  private stumpTplPos!: Float32Array;
  private stumpTplNorm!: Float32Array;
  private stumpVerts = 0;
  private stumpCount = 0;

  /** Collect a built tree (already positioned/scaled in world by its Scene.ts maker). */
  add(type: TreeType, obj: THREE.Object3D, x: number, z: number, gy: number) {
    if (!this.pending.has(type)) this.pending.set(type, []);
    this.pending.get(type)!.push({ obj, x, z, gy });
  }

  /** The raw tree objects, for the ?nomerge debug path (no packing, no felling). */
  rawObjects(): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    for (const list of this.pending.values()) for (const t of list) out.push(t.obj);
    return out;
  }

  /** Pack every collected tree into per-type slot buffers and build the (empty) stump buffer. */
  build(): THREE.Group {
    let total = 0;
    for (const [type, list] of this.pending) { total += list.length; this.packType(type, list); }
    this.buildStumps(total);
    return this.group;
  }

  private packType(type: TreeType, list: { obj: THREE.Object3D; x: number; z: number; gy: number }[]) {
    if (!list.length) return;
    const n = list.length;
    // Establish the bucket layout (material order + per-bucket slot vertex count) from tree 0.
    const proto = bakeTree(list[0].obj);
    const buckets: Bucket[] = proto.map(({ geo }) => {
      const slotVerts = geo.getAttribute('position').count;
      const attrs: PackedAttr[] = [];
      const make = (name: PackedAttr['name'], comp: number) => {
        const arr = new Float32Array(n * slotVerts * comp);
        const attr = new THREE.BufferAttribute(arr, comp);
        attr.setUsage(THREE.DynamicDrawUsage);
        attrs.push({ name, comp, arr, attr });
      };
      make('position', 3);
      make('normal', 3);
      if (geo.getAttribute('uv')) make('uv', 2);
      if (geo.getAttribute('color')) make('color', 3);
      const outGeo = new THREE.BufferGeometry();
      for (const a of attrs) outGeo.setAttribute(a.name, a.attr);
      return { slotVerts, attrs, geo: outGeo };
    });

    const slotToHandle: Handle[] = [];
    let slot = 0;
    for (const item of list) {
      const baked = slot === 0 ? proto : bakeTree(item.obj);
      // Guard: every tree of a type must match the prototype's bucket vertex counts (they do,
      // because the builders are topology-deterministic). Skip any that somehow don't, rather than
      // corrupting the packed buffer.
      let okShape = baked.length === buckets.length;
      if (okShape) for (let b = 0; b < buckets.length; b++) {
        if (baked[b].geo.getAttribute('position').count !== buckets[b].slotVerts) { okShape = false; break; }
      }
      if (!okShape) { console.warn('[Forest] tree shape mismatch; skipped', type); continue; }
      for (let b = 0; b < buckets.length; b++) {
        const bk = buckets[b], src = baked[b].geo;
        for (const a of bk.attrs) {
          const s = src.getAttribute(a.name) as THREE.BufferAttribute;
          a.arr.set(s.array as Float32Array, slot * bk.slotVerts * a.comp);
        }
      }
      slotToHandle.push({ x: item.x, z: item.z, gy: item.gy, slot });
      slot++;
    }
    const live = slot;
    for (const bk of buckets) bk.geo.setDrawRange(0, live * bk.slotVerts);
    // Build one mesh per bucket, reusing the source material (shared singleton) so lighting matches.
    proto.forEach(({ mat }, b) => {
      const mesh = new THREE.Mesh(buckets[b].geo, mat);
      mesh.frustumCulled = false; // spans the whole map, like the old global merge
      this.group.add(mesh);
    });
    this.packs.set(type, { buckets, live, slotToHandle });
  }

  private buildStumps(capacity: number) {
    const tpl = stumpTemplate();
    this.stumpTplPos = (tpl.getAttribute('position') as THREE.BufferAttribute).array as Float32Array;
    this.stumpTplNorm = (tpl.getAttribute('normal') as THREE.BufferAttribute).array as Float32Array;
    this.stumpVerts = tpl.getAttribute('position').count;
    const cap = Math.max(1, capacity);
    this.stumpPos = new THREE.BufferAttribute(new Float32Array(cap * this.stumpVerts * 3), 3);
    this.stumpNorm = new THREE.BufferAttribute(new Float32Array(cap * this.stumpVerts * 3), 3);
    this.stumpPos.setUsage(THREE.DynamicDrawUsage);
    this.stumpNorm.setUsage(THREE.DynamicDrawUsage);
    this.stumpGeo = new THREE.BufferGeometry();
    this.stumpGeo.setAttribute('position', this.stumpPos);
    this.stumpGeo.setAttribute('normal', this.stumpNorm);
    this.stumpGeo.setDrawRange(0, 0);
    const mesh = new THREE.Mesh(this.stumpGeo, STUMP_MAT);
    mesh.frustumCulled = false;
    this.group.add(mesh);
  }

  private addStump(x: number, gy: number, z: number) {
    if (this.stumpCount * this.stumpVerts * 3 >= this.stumpPos.array.length) return; // capacity guard
    const base = this.stumpCount * this.stumpVerts * 3;
    const pos = this.stumpPos.array as Float32Array;
    const nrm = this.stumpNorm.array as Float32Array;
    for (let i = 0; i < this.stumpTplPos.length; i += 3) {
      pos[base + i] = this.stumpTplPos[i] + x;
      pos[base + i + 1] = this.stumpTplPos[i + 1] + gy;
      pos[base + i + 2] = this.stumpTplPos[i + 2] + z;
    }
    nrm.set(this.stumpTplNorm, base);
    const count = this.stumpVerts * 3;
    this.stumpPos.addUpdateRange(base, count); this.stumpPos.needsUpdate = true;
    this.stumpNorm.addUpdateRange(base, count); this.stumpNorm.needsUpdate = true;
    this.stumpCount++;
    this.stumpGeo.setDrawRange(0, this.stumpCount * this.stumpVerts);
  }

  /** Fell every live tree within `radius` of (x, z). Returns the felled trees' base positions
   *  (for the caller to spawn leaf/dust FX). Each fell is a one-slot swap-remove + one stump. */
  fell(x: number, z: number, radius: number): THREE.Vector3[] {
    const felled: THREE.Vector3[] = [];
    const r2 = radius * radius;
    for (const pack of this.packs.values()) {
      let slot = 0;
      while (slot < pack.live) {
        const h = pack.slotToHandle[slot];
        const dx = h.x - x, dz = h.z - z;
        if (dx * dx + dz * dz <= r2) {
          felled.push(new THREE.Vector3(h.x, h.gy, h.z));
          this.removeSlot(pack, slot);   // swaps the last live slot into `slot`; re-test it next loop
          this.addStump(h.x, h.gy, h.z);
        } else {
          slot++;
        }
      }
    }
    return felled;
  }

  private removeSlot(pack: TypePack, slot: number) {
    const last = pack.live - 1;
    if (slot !== last) {
      for (const bk of pack.buckets) {
        for (const a of bk.attrs) {
          const s = bk.slotVerts * a.comp; // floats per slot for this attribute
          a.arr.copyWithin(slot * s, last * s, last * s + s);
          a.attr.addUpdateRange(slot * s, s);
          a.attr.needsUpdate = true;
        }
      }
      pack.slotToHandle[slot] = pack.slotToHandle[last];
      pack.slotToHandle[slot].slot = slot;
    }
    pack.slotToHandle.length = last;
    pack.live = last;
    for (const bk of pack.buckets) bk.geo.setDrawRange(0, pack.live * bk.slotVerts);
  }
}
